/* React imports */
import { useContext, useEffect, useState } from "react";

/* Third party imports */
import { useMutation } from "react-query";

/* Local imports */
import StepCareer from "./stepCareer";
import StepName from "./stepName";
import StepNeeds from "./stepNeeds";
import {
  StepBlueCareerParameters,
  StepBlueNeedsParameters,
  StepCareerParameters,
  StepJourneyParameters,
  StepNameParameters,
  StepNeedsParameters,
  UserDetails,
} from "./types";
import { updateUserDetails } from "../../../services/onboarding.service";
import SubmitStep from "./submitStep";
import { AuthContext } from "../../../contexts/AuthContext";
import { PublicFooter } from "../../../components/publicFooter";
import { useJourneyData } from "./utils/journeyUtils";
import { ReactComponent as Logo } from "../../../assets/restart_logo.svg";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import StepBlueCareer from "./stepBlueCareer";
import StepBlueNeeds from "./stepBlueNeeds";

export function Onboarding() {
  const { setUser, configurations } = useContext(AuthContext);
  const { t } = useTranslation("onboarding");

  const renderCareerInformationStep =
    configurations?.onboardingCareerInformation;
  const renderCareerExpectationStep =
    configurations?.onboardingCareerExpectations;

  const stepRenderContent = configurations?.onboardingProductInfo;

  const productType = configurations?.productType;

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [nameStepInfo, setNameStepInfo] = useState<StepNameParameters>({
    firstName: "",
    lastName: "",
    birthday: null,
    mobilePhone: "",
    preferredLanguage: "",
    city: null,
  });

  const [journeyStepInfo, setJourneyStepInfo] = useState<StepJourneyParameters>(
    {
      journeyId: "",
    }
  );
  const [careerStepInfo, setCareerStepInfo] = useState<StepCareerParameters>({
    lastPosition: null,
    careerLevel: null,
    industry: null,
    discipline: null,
  });

  const [blueCareerStepInfo, setBlueCareerStepInfo] =
    useState<StepBlueCareerParameters>({
      lastPosition: null,
      experienceInYears: null,
      industry: null,
      jobHistories: [],
    });

  const [needsStepInfo, setNeedsStepInfo] = useState<StepNeedsParameters>({
    timingLastDayAtWork: null,
    timingAchieveRestart: null,
    need: "",
  });

  const [blueNeedsStepInfo, setBlueNeedsStepInfo] =
    useState<StepBlueNeedsParameters>({
      timingLastDayAtWork: null,
      timingAchieveRestart: null,
      need: "",
      whatsappOptIn: !!nameStepInfo.mobilePhone,
      mobilePhone: nameStepInfo.mobilePhone || "",
    });
  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    undefined
  );
  const [loading, setLoading] = useState<boolean>(false);
  const journeyData = useJourneyData();

  useEffect(() => {
    setBlueNeedsStepInfo((prev) => ({
      ...prev,
      whatsappOptIn: !!nameStepInfo.mobilePhone,
      mobilePhone: nameStepInfo.mobilePhone || "",
    }));
  }, [nameStepInfo.mobilePhone]);

  const handleNameNextStep = (data: StepNameParameters) => {
    setNameStepInfo(data);
    skipStep();
  };

  const handleJourneyNextStep = (data: StepJourneyParameters) => {
    setJourneyStepInfo(data);
    setSelectedOption(data?.journeyId);
    skipStep();
  };

  const handleCareerNextStep = (data: StepCareerParameters) => {
    setCareerStepInfo(data);
    skipStep();
  };

  const handleNeedsNextStep = (data: StepNeedsParameters) => {
    setNeedsStepInfo(data);
    skipStep();
  };

  const handleBlueNeedsNextStep = (data: StepBlueNeedsParameters) => {
    setBlueNeedsStepInfo(data);
    skipStep();
  };

  const handleBlueCareerNextStep = (data: StepBlueCareerParameters) => {
    setBlueCareerStepInfo(data);
    skipStep();
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipStep = () => setCurrentStep(currentStep + 1);

  useEffect(() => {
    if (currentStep === 4 && !stepRenderContent) {
      handleSubmit();
    }
  }, [currentStep, stepRenderContent]);

  const handleSubmit = async () => {
    const updatedNameStepInfo = Object.fromEntries(
      Object.entries(nameStepInfo).map(([key, value]) => {
        return [key, value === "" ? null : value];
      })
    );

    const updatedCareerStepInfo = Object.fromEntries(
      Object.entries(careerStepInfo).map(([key, value]) => {
        return [key, value === "" ? null : value];
      })
    );

    const updatedBlueCareerStepInfo = Object.fromEntries(
      Object.entries(blueCareerStepInfo).map(([key, value]) => {
        return [key, value === "" ? null : value];
      })
    );

    const updatedNeedsStepInfo = Object.fromEntries(
      Object.entries(needsStepInfo).map(([key, value]) => {
        return [key, value === "" ? null : value];
      })
    );

    const updatedBlueNeedsStepInfo = Object.fromEntries(
      Object.entries(blueNeedsStepInfo).map(([key, value]) => {
        return [key, value === "" ? null : value];
      })
    );

    const payload: UserDetails = {
      ...updatedNameStepInfo,
      journeyId: journeyData.standardUserJourneyId, // ...journeyStepInfo
      ...(productType === "BLUE"
        ? updatedBlueCareerStepInfo
        : updatedCareerStepInfo),
      ...(productType === "BLUE"
        ? updatedBlueNeedsStepInfo
        : updatedNeedsStepInfo),

      othersDiscipline: null,
      othersIndustry: null,
    };

    if (nameStepInfo.city) {
      payload.jobSearchCity = nameStepInfo.city;
    }

    try {
      console.log("+++", payload);
      setLoading(true);
      // handle success
      await submitNewUserDetails.mutate({ userDetails: payload });
    } catch (error) {
      console.log("ERROR: ", error);
    }
  };

  const submitNewUserDetails = useMutation(updateUserDetails, {
    onSuccess: (response) => {
      setUser(response);
      setLoading(false);
    },
    onError: (error: any) => {
      console.log("Error: ", error);
      setLoading(false);
    },
  });

  let content;
  switch (currentStep) {
    case 1:
      content = (
        <StepName
          onNext={handleNameNextStep}
          onSkip={skipStep}
          initialData={nameStepInfo}
        />
      );
      break;
    /*case 2:
      content = (
        <>
          {journeyData ? (
            <StepJourney
              onNext={handleJourneyNextStep}
              onSkip={skipStep}
              availableJourneyIds={journeyData}
            />
          ) : null}
        </>
      );
      break;*/
    case 2:
      if (renderCareerInformationStep) {
        if (productType === "BLUE") {
          content = (
            <StepBlueCareer
              onNext={handleBlueCareerNextStep}
              onSkip={skipStep}
              onBack={goBack}
              initialData={blueCareerStepInfo}
            />
          );
        } else {
          content = (
            <StepCareer
              onNext={handleCareerNextStep}
              onSkip={skipStep}
              onBack={goBack}
              initialData={careerStepInfo}
            />
          );
        }
      } else {
        skipStep(); // Skip this step entirely if configuration flag is false
      }
      break;
    case 3: // 4
      if (renderCareerExpectationStep) {
        if (productType === "BLUE") {
          content = (
            <StepBlueNeeds
              onNext={handleBlueNeedsNextStep}
              onSkip={skipStep}
              onBack={goBack}
              selectedOption={journeyData.standardUserJourneyId} // ={selectedOption}
              availableJourneyIds={journeyData}
              initialMobilePhone={nameStepInfo.mobilePhone}
              initialData={blueNeedsStepInfo}
            />
          );
        } else {
          content = (
            <StepNeeds
              onNext={handleNeedsNextStep}
              onSkip={skipStep}
              onBack={goBack}
              selectedOption={journeyData.standardUserJourneyId} // ={selectedOption}
              availableJourneyIds={journeyData}
              initialData={needsStepInfo}
            />
          );
        }
      } else {
        skipStep(); // Skip to the next step
      }

      break;
    case 4: // 5
      if (stepRenderContent) {
        content = (
          <SubmitStep
            onNext={handleSubmit}
            onBack={goBack}
            selectedOption={journeyData.standardUserJourneyId} // ={selectedOption}
            availableJourneyIds={journeyData}
          />
        );
      }
      break;
    default:
      content = <div>Invalid step</div>;
  }

  return (
    <div className="flex flex-col h-screen overflow-auto">
      <div
        className={`flex w-full inset-x-0 top-0 items-center p-4 justify-center  bg-Grey-navbar`}
      >
        <div />
        <a href="/" className="cursor-pointer">
          <Logo className="h-10" />
        </a>
      </div>
      {!loading && (
        <div className="flex justify-center">
          <div className="flex justify-center w-full flex-grow pt-6 px-6 md:pt-14">
            <div className="w-full md:w-1/2 lg:w-1/3">{content}</div>
          </div>
        </div>
      )}
      {loading && (
        <div className="flex flex-col justify-center items-center w-full h-1/2 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-300 text-gray-400 mx-auto mt-8 mb-8">
          <CircularProgress style={{ color: "#DCAA00" }} />
          <p className="text-3xl mt-8 font-bold mb-2 text-black animate-pulse">
            {t("loading")}
          </p>
          <p className="mt-2 text-lg text-black">{t("please-wait")}</p>
        </div>
      )}

      <div className="mt-auto	">
        <PublicFooter />
      </div>
    </div>
  );
}
